import { useLazyQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { ContactMethodType } from '~/components/partials/PatientDetailDialog/NewMedicationFollowupMessagePane/types';
import { DEFAULT_AUTO_REPLY_MESSAGE } from '~/constants/medication_followup_message_template';
import {
  GetFollowupDraftMessageByCurrentPractitionerDocument,
  GetFollowupMessageDocument,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { useMedicationFollowupQuestionnaires } from '~/hooks/use-medication-followup-questionnaires';
import {
  patientDetailDialogMessageState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';
import { removeLastUrl } from '~/utils/medication_followup_message';

import { Fields } from './InputPane/types';
import { Patient } from './types';

export const useDefaultValues = (
  selectMessageId: string | null,
  patient: Patient,
  isEditable: boolean,
) => {
  const [getMessage, { loading: loadingMessage, data: messageData }] = useLazyQuery(
    GetFollowupMessageDocument,
    { fetchPolicy: 'network-only' },
  );
  const { questionnaires } = useMedicationFollowupQuestionnaires();
  const message = getNode(messageData, 'MedicationFollowupMessage');
  const isFailed = message?.messageDeliverResult?.status === 'failed';
  const contactMethod: ContactMethodType | null = useMemo(() => {
    if (!isEditable) return null;
    return message?.patient?.id ? 'APP' : patient.type === 'LineVisitor' ? 'LINE' : 'SMS';
  }, [isEditable, message?.patient?.id, patient.type]);
  const { eventId } = useAtomValue(patientDetailDialogState);

  const setPatientDetailDialogMessageState = useSetAtom(patientDetailDialogMessageState);
  const [isLoadedDraftMessage, setIsLoadedDraftMessage] = useState(false);
  const [
    getFollowupDraftMessageByCurrentPractitioner,
    { loading: loadingDraftMessage, data: draftMessageData },
  ] = useLazyQuery(GetFollowupDraftMessageByCurrentPractitionerDocument, {
    fetchPolicy: 'network-only',
  });
  const draftMessage = useMemo(() => {
    const node =
      getNode(draftMessageData, 'Patient') ||
      getNode(draftMessageData, 'LineVisitor') ||
      getNode(draftMessageData, 'WebVisitor') ||
      getNode(draftMessageData, 'DirectVisitor');

    return (
      node?.medixsPatient?.medicationFollowupDraftMessage ||
      node?.medicationFollowupDraftMessages.nodes[0] ||
      null
    );
  }, [draftMessageData]);

  useEffect(() => {
    if (!draftMessageData) return;

    const node =
      getNode(draftMessageData, 'Patient') ||
      getNode(draftMessageData, 'LineVisitor') ||
      getNode(draftMessageData, 'WebVisitor') ||
      getNode(draftMessageData, 'DirectVisitor');

    const medicationFollowupDraftMessageId =
      node?.medixsPatient?.medicationFollowupDraftMessage?.id ||
      node?.medicationFollowupDraftMessages.nodes[0]?.id ||
      null;

    setIsLoadedDraftMessage(true);
    setPatientDetailDialogMessageState((state) => ({
      ...state,
      draftMessageId:
        medicationFollowupDraftMessageId ||
        node?.medicationFollowupDraftMessages.nodes[0]?.id ||
        null,
    }));
  }, [draftMessageData, setPatientDetailDialogMessageState]);

  const defaultClinic = useMemo(() => {
    if (isEditable) {
      return message?.csClinic;
    } else {
      return draftMessage?.data.csClinic;
    }
  }, [draftMessage?.data.csClinic, isEditable, message?.csClinic]);
  const defaultQuestionnaire = useMemo(() => {
    const firstQuestionnaire = questionnaires ? questionnaires[0] : null;
    return (
      message?.medicationFollowupQuestionnaireRevision?.medicationFollowupQuestionnaire ??
      firstQuestionnaire
    );
  }, [
    message?.medicationFollowupQuestionnaireRevision?.medicationFollowupQuestionnaire,
    questionnaires,
  ]);
  const defaultQuestionnaireId = defaultQuestionnaire?.id || '';

  const defaultMedixsEvent = useMemo(() => {
    return getNode(messageData, 'MedicationFollowupMessage')?.medixsEvent || null;
  }, [messageData]);

  const initialValues: Fields = {
    attachment: 'questionnaire_sheet',
    csClinicId: '',
    medicationFollowupQuestionnaireId: defaultQuestionnaireId,
    fixed: true,
    sendDate: null,
    sendTime: '',
    message: '',
    isEnabledAutoReplySetting: false,
    autoReplyMessage: DEFAULT_AUTO_REPLY_MESSAGE,
    withAutoReplyQuestionnaire: false,
    medixsEventId: undefined,
    startingPoint: null,
  };

  const defaultValues: Fields = useMemo(() => {
    if (selectMessageId && message) {
      const sendDate = new Date(message.sendAt);
      const sendTime = format(sendDate, 'HH');

      return {
        attachment: message.medicationFollowupQuestionnaireRevision
          ? 'questionnaire_sheet'
          : message.csClinic
            ? 'clinic'
            : '',
        csClinicId: message.csClinic?.id ?? '',
        medicationFollowupQuestionnaireId:
          message.medicationFollowupQuestionnaireRevision?.medicationFollowupQuestionnaire.id ||
          defaultQuestionnaireId,
        sendDate,
        sendTime,
        fixed: true,
        message: message.csClinic ? removeLastUrl(message.message) : message.message,
        isEnabledAutoReplySetting: !!message.autoReplySetting,
        autoReplyMessage: message.autoReplySetting?.message || DEFAULT_AUTO_REPLY_MESSAGE,
        withAutoReplyQuestionnaire: !!message.autoReplySetting?.questionnaireRevision,
        medixsEventId: message?.medixsEvent?.eventId || null,
        startingPoint: message.startingPoint || null,
      };
    }

    if (eventId) {
      return {
        ...initialValues,
        medixsEventId: eventId,
      };
    }

    if (isLoadedDraftMessage && draftMessage) {
      const data = draftMessage.data;
      const attachment = data.medicationFollowupQuestionnaire?.id
        ? 'questionnaire_sheet'
        : data.checkboxRecommendation
          ? 'clinic'
          : '';

      return {
        attachment,
        csClinicId: data.csClinic?.id || '',
        medicationFollowupQuestionnaireId:
          data.medicationFollowupQuestionnaire?.id || defaultQuestionnaireId,
        fixed: data.fixedDate,
        sendDate: data.sendDate ? new Date(data.sendDate) : null,
        sendTime: data.sendTime || '',
        message: data.message || '',
        isEnabledAutoReplySetting: data.checkboxAutoReplySetting,
        autoReplyMessage: data.autoReplySettingMessage || '',
        withAutoReplyQuestionnaire: data.checkboxAutoReplySettingQuestionnaire,
        medixsEventId: data.medixsEventId || null,
        startingPoint: data.startingPoint || null,
      };
    }

    return initialValues;

    // despにdraftMessageを入れると、自動保存APIリクエストの度にこのuseMemoが動いてしまい、メッセージ入力がスムーズにできなくなるので入れない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultQuestionnaire?.id, isLoadedDraftMessage, message, selectMessageId]);

  useEffect(() => {
    if (!isEditable) return;

    if (selectMessageId) {
      getMessage({
        variables: {
          id: selectMessageId,
        },
      });
    }
  }, [getMessage, isEditable, selectMessageId]);

  useEffect(() => {
    if (isEditable) return;

    if (patient.id) {
      getFollowupDraftMessageByCurrentPractitioner({
        variables: {
          id: patient.id,
        },
      });
    }
  }, [getFollowupDraftMessageByCurrentPractitioner, isEditable, patient.id]);

  return {
    failed: isFailed,
    loadingMessage,
    loadingDraftMessage,
    defaultClinic,
    defaultQuestionnaire,
    defaultMedixsEvent,
    defaultValues,
    initialValues,
    contactMethod,
  };
};
